import React, { FC } from "react";
import { useTheme, withThemeProvider } from "@narvar/nth-hook-theme";
import Icon from "../Icon";
import ItemImage from "../ItemImage/ItemImage";
import Language from "../Language";
import Tag from "../Tag/Tag";
import { useStyles } from "./ItemCardStyles";
import { ItemCardProps } from "./types";
import StyledMarkup from "../StyledMarkup";
import SelectItem from "../SelectItem/SelectItem";
import {
  ReturnReasonsReturnType,
  useReturnsConfig,
} from "@narvar/nth-kit-returns-headless";
import Chip from "../Chip/Chip";
import Tooltip from "../Tooltip/Tooltip";

const typeKey: Record<ReturnReasonsReturnType, string> = {
  return: "returnsItemListItemTagReturning",
  exchange: "returnsItemListItemTagExchanging",
  exchangeForCredit: "returnsItemListItemTagExchangingForCredit",
  claim: "returnsItemListItemTagClaiming",
};

const ItemCardC: FC<ItemCardProps> = ({
  thumbnail,
  title,
  subtitle,
  attributes,
  additionalInfo,
  onClick,
  selected,
  disabled,
  clickable,
  sTag,
  sDefault,
  layout,
  borderless,
  returnType,
  hasShippingProtection,
}) => {
  const { bodytextDefaultNone, headersMediumNone } = useTheme();
  const { fitItemThumbnail, fitItemThumbnailPosition } = useReturnsConfig();

  const styles = useStyles();

  return (
    <Tag
      tagContent={
        selected && returnType ? (
          <>
            <Language langKey={typeKey[returnType]} />
            <Icon
              icon="CheckMark"
              fill="#ffffff"
              width="11"
              height="11"
              style={{ marginLeft: "8px" }}
            />
          </>
        ) : null
      }
      s={{ fontFamily: bodytextDefaultNone?.fontFamily, zIndex: 10, ...sTag }}
      anchor={{ vertical: "top", horizontal: "left" }}>
      <SelectItem
        onClick={() => {
          if (clickable && onClick) {
            onClick();
          }
        }}
        customStyle={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          width: "100%",
          textAlign: layout && layout === "vertical" ? "center" : "left",
          flexDirection: layout && layout === "vertical" ? "column" : "row",
          ...(disabled ? { opacity: 0.5 } : {}),
          ...sDefault,
        }}
        selected={selected}
        readOnly={!clickable || disabled}
        borderless={borderless}>
        <ItemImage
          src={thumbnail}
          alt={title || ""}
          fit={fitItemThumbnail}
          objectPosition={fitItemThumbnailPosition}
        />
        <div
          className={styles.itemInfo}
          style={{
            alignItems:
              layout && layout === "vertical" ? "center" : "flex-start",
          }}>
          {hasShippingProtection && (
            <Chip
              fill="rgba(40, 167, 69, 0.10)"
              color={bodytextDefaultNone?.color ?? "#0E0E0E"}
              rounded="large"
              icon={<Icon icon="ShippingProtection" />}
              action={
                <Tooltip
                  title={
                    <Language langKey="returnsItemListItemTagHasShippingProtectionTooltip" />
                  }>
                  <Icon icon="HelpCircle" />
                </Tooltip>
              }>
              <Language langKey="returnsItemListItemTagHasShippingProtection" />
            </Chip>
          )}
          <h4 className={styles.itemName} style={headersMediumNone}>
            <StyledMarkup content={title} />
          </h4>
          {subtitle && (
            <p
              className={styles.itemInfoDetail}
              style={{
                justifyContent:
                  layout && layout === "vertical" ? "center" : "flex-start",
                padding: 0,
                fontWeight: "700",
                fontSize: bodytextDefaultNone?.fontSize ?? "14px",
                lineHeight: bodytextDefaultNone?.lineHeight ?? "20px",
              }}>
              <StyledMarkup content={subtitle} />
            </p>
          )}
          {attributes?.map((attr, i) => (
            <p
              className={styles.itemInfoDetail}
              style={{
                justifyContent:
                  layout && layout === "vertical" ? "center" : "flex-start",
                padding: 0,
                fontSize: bodytextDefaultNone?.fontSize ?? "14px",
                lineHeight: bodytextDefaultNone?.lineHeight ?? "20px",
              }}
              key={attr.key ?? `detail-${i}`}>
              {attr.key}
              {attr.transaction ? (
                <>
                  <s style={{ color: "#999999" }}>{attr.transaction.before}</s>
                  <Icon icon="ArrowRight" height="20" width="20" />
                  {attr.transaction.after}
                </>
              ) : (
                attr.value
              )}
            </p>
          ))}
          {additionalInfo && (
            <p
              className={styles.itemInfoDetail}
              style={{
                justifyContent:
                  layout && layout === "vertical" ? "center" : "flex-start",
                padding: "4px 0 0 0",
                fontWeight: "700",
                fontSize: bodytextDefaultNone?.fontSize ?? "14px",
                lineHeight: bodytextDefaultNone?.lineHeight ?? "20px",
              }}>
              <StyledMarkup content={additionalInfo} />
            </p>
          )}
        </div>
      </SelectItem>
    </Tag>
  );
};

export const ItemCard = withThemeProvider(ItemCardC, {
  themeTokens: [
    "bodytextDefaultNone",
    "captiontextDefaultNone",
    "headersMediumNone",
    "headersSmallNone",
  ],
});
